// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  linkWithPopup,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_APP_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_APP_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication with persistence fallback chain.
// Si IndexedDB falla (QuotaExceededError, modo restrictivo, etc.), Firebase
// Auth intenta el siguiente mecanismo en orden hasta encontrar uno que escriba.
// inMemoryPersistence es el último recurso: la sesión vive solo en RAM, pero
// permite que la app funcione hasta que el usuario refresque.
export const auth = initializeAuth(app, {
  persistence: [
    indexedDBLocalPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
    inMemoryPersistence,
  ],
  // IMPRESCINDIBLE para que signInWithPopup (Google) funcione cuando se usa
  // initializeAuth manual. Sin esto, Firebase tira auth/argument-error en el popup.
  popupRedirectResolver: browserPopupRedirectResolver,
});

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);

// Initialize Cloud Functions
export const functions = getFunctions(app, 'southamerica-east1');


// Connect to emulator if in development (optional)
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATOR === 'true') {
  connectFunctionsEmulator(functions, "localhost", 5001);
}

// Google Auth Provider con configuración para prevenir reemplazo de proveedores
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// IMPORTANTE: Esta configuración NO previene el reemplazo de proveedores
// Firebase Auth no tiene una configuración para esto
// La ÚNICA manera de prevenirlo es usar linkWithPopup() en lugar de signInWithPopup()
// cuando el usuario ya tiene una cuenta con otro proveedor

// Auth functions con verificación previa de email
export const signInWithGoogle = async () => {
  try {
    // Hacer el sign-in con Google
    const result = await signInWithPopup(auth, googleProvider);
    const email = result.user.email;

    console.log('✅ Google popup completado para:', email);

    // IMPORTANTE: Después del sign-in, verificar si Firebase reemplazó el provider
    const providerIds = result.user.providerData.map(p => p.providerId);
    console.log('🔐 Proveedores actuales en Auth:', providerIds);

    // Si el usuario SOLO tiene Google ahora, pero el sign-in fue exitoso,
    // significa que podría haber reemplazado un provider anterior

    return result;
  } catch (error) {
    // Si el error es porque la cuenta existe con otro proveedor
    if (error.code === 'auth/account-exists-with-different-credential') {
      const email = error.customData?.email || error.email;
      console.error('⚠️ Cuenta existe con otro proveedor:', email);

      // Re-lanzar el error con información adicional
      const customError = new Error('Esta cuenta ya existe con otro método de inicio de sesión.');
      customError.code = 'auth/account-exists-with-different-credential';
      customError.email = email;
      customError.credential = error.credential;
      throw customError;
    }

    console.error('Error durante login con Google:', error);
    throw error;
  }
};

// Función para vincular Google a cuenta existente
export const linkGoogleToAccount = async (user) => {
  try {
    const result = await linkWithPopup(user, googleProvider);
    console.log('✅ Google vinculado exitosamente a la cuenta');
    return result;
  } catch (error) {
    console.error('Error vinculando Google a la cuenta:', error);
    throw error;
  }
};

// Función para verificar métodos de inicio de sesión para un email
export const checkSignInMethods = async (email) => {
  try {
    const methods = await fetchSignInMethodsForEmail(auth, email);
    return methods;
  } catch (error) {
    console.error('Error verificando métodos de inicio de sesión:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error durante logout:', error);
    throw error;
  }
};

export default app;